import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ALL_CATEGORY, HealthPills, HealthTable } from '@/components/tables/health-table'
import { TableSkeleton } from '@/components/tables/issue-table'
import { ViewHeader } from '@/components/shell/view-header'
import { useViewFilters } from '@/hooks/use-view-filters'
import { useIssues } from '@/lib/query'
import { ViewError } from '@/components/common/query-states'
import { MISSING_METADATA, SYSTEM_LABEL_PRECEDENCE, healthCategory } from '@/lib/stage'
import { HEALTH_VIEW } from '@/lib/views'

// Pill order: known system labels by urgency, then any unrecognised system
// label, then the noisy 'missing metadata' bucket last.
function categoryRank(category: string): number {
  const i = (SYSTEM_LABEL_PRECEDENCE as readonly string[]).indexOf(category)
  if (i !== -1) return i
  if (category === MISSING_METADATA) return 100
  return 50
}

export default function Health() {
  const { data: issues, isPending, isError, error, refetch } = useIssues()
  const chipFilter = useViewFilters()
  const [searchParams, setSearchParams] = useSearchParams()

  const items = useMemo(
    () => chipFilter.apply(HEALTH_VIEW.pool(issues ?? [], {})),
    [issues, chipFilter],
  )

  const { categories, counts } = useMemo(() => {
    const counts = new Map<string, number>()
    for (const issue of items) {
      const category = healthCategory(issue)
      counts.set(category, (counts.get(category) ?? 0) + 1)
    }
    const categories = [...counts.keys()].sort(
      (a, b) => categoryRank(a) - categoryRank(b) || a.localeCompare(b),
    )
    return { categories, counts }
  }, [items])

  // URL-driven so the view is shareable; an unknown/empty category falls back to
  // 'all' rather than showing an empty table.
  const requested = searchParams.get('cat')
  const selected = requested && categories.includes(requested) ? requested : ALL_CATEGORY

  const setSelected = (category: string) =>
    setSearchParams(
      (p) => {
        const next = new URLSearchParams(p)
        if (category === ALL_CATEGORY) next.delete('cat')
        else next.set('cat', category)
        return next
      },
      { replace: true },
    )

  const shown = useMemo(
    () =>
      selected === ALL_CATEGORY ? items : items.filter((i) => healthCategory(i) === selected),
    [items, selected],
  )

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title="Health"
        meta={
          <span className="font-mono text-[11px]">
            label:system/* or missing metadata · {items.length} items
          </span>
        }
      />
      {isError ? (
        <ViewError what="issues" error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        <TableSkeleton />
      ) : (
        <>
          <HealthPills
            categories={categories}
            counts={counts}
            total={items.length}
            selected={selected}
            onSelect={setSelected}
          />
          <HealthTable issues={shown} />
        </>
      )}
    </div>
  )
}
